/**
 * Shared chart clock: Binance futures candles are UTC unix seconds;
 * every UI axis / tooltip must render Asia/Tehran.
 */

export const CHART_TIMEZONE = 'Asia/Tehran'

/** lightweight-charts TickMarkType values (numeric enum). */
const TICK = {
  Year: 0,
  Month: 1,
  DayOfMonth: 2,
  Time: 3,
  TimeWithSeconds: 4,
}

/**
 * @param {number} unixSeconds
 * @param {Intl.DateTimeFormatOptions} options
 * @return {string}
 */
export function formatInTehran(unixSeconds, options = {}) {
  if (!Number.isFinite(unixSeconds)) return ''

  return new Intl.DateTimeFormat('en-GB', {
    timeZone: CHART_TIMEZONE,
    hour12: false,
    ...options,
  }).format(new Date(unixSeconds * 1000))
}

/**
 * Crosshair / tooltip datetime in Tehran.
 *
 * @param {number} unixSeconds
 * @return {string}
 */
export function formatChartDateTime(unixSeconds) {
  return formatInTehran(unixSeconds, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Axis tick labels (Tehran).
 *
 * @param {number} unixSeconds
 * @param {number} tickMarkType
 * @return {string}
 */
export function formatChartTickMark(unixSeconds, tickMarkType) {
  if (tickMarkType === TICK.Year) {
    return formatInTehran(unixSeconds, { year: 'numeric' })
  }

  if (tickMarkType === TICK.Month) {
    return formatInTehran(unixSeconds, { month: 'short' })
  }

  if (tickMarkType === TICK.DayOfMonth) {
    return formatInTehran(unixSeconds, { day: '2-digit', month: 'short' })
  }

  if (tickMarkType === TICK.TimeWithSeconds) {
    return formatInTehran(unixSeconds, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  return formatInTehran(unixSeconds, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Options merged into lightweight-charts createChart() so the whole pane
 * (axis + crosshair) uses Tehran instead of UTC / browser local.
 *
 * @return {{ localization: object, timeScale: object }}
 */
export function tehranChartClockOptions() {
  return {
    localization: {
      locale: 'en-GB',
      timeFormatter: (time) => formatChartDateTime(Number(time)),
    },
    timeScale: {
      tickMarkFormatter: (time, tickMarkType) =>
        formatChartTickMark(Number(time), tickMarkType),
    },
  }
}
